import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Search,
  MapPin,
  Star,
  CheckCircle2,
  Filter,
  ArrowRight,
  Phone,
  Mail,
  ExternalLink,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/lib/i18n";
import { getPublicProviders } from "@/lib/public-content.functions";
import { DirectoryNavigation } from "@/components/directory-navigation";
import { directoryTrades } from "@/data/directory-pages";
import { providerCantonIds, providerMatchesSpecialty, providerWebsite } from "@/lib/directory";
import { directoryHead } from "@/lib/directory-seo";

export const Route = createFileRoute("/auftragnehmer")({
  staticData: { sitemap: true },
  loader: () => getPublicProviders({ data: { type: "auftragnehmer" } }),
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; category?: string; canton?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
    category: typeof search.category === "string" ? search.category : undefined,
    canton: typeof search.canton === "string" ? search.canton : undefined,
  }),
  head: ({ loaderData }) =>
    directoryHead(loaderData ? { kind: "contractor", ...loaderData } : undefined),
  component: AuftragnehmerPage,
});

type Contractor = {
  id?: string;
  name: string;
  categoryId: string;
  categoryIds?: string[];
  category: string;
  city: string;
  cantonId: string;
  cantonIds?: string[];
  canton: string;
  serviceArea?: string;
  rating: number;
  reviews?: number;
  reviewSource?: string;
  reviewsUrl?: string;
  verified: boolean;
  years?: number;
  description: string;
  tags: string[];
  phone?: string;
  email?: string;
  address?: string;
  website?: string;
};

const contractorCopy = {
  de: {
    eyebrow: "Handwerkerverzeichnis",
    title: "Geprüfte Auftragnehmer in der Schweiz",
    introBeforeCount: "Vergleichen Sie",
    introAfterCount:
      "Bauunternehmen anhand echter Kundenbewertungen, Erfahrung und Region. Fordern Sie kostenlos unverbindliche Offerten an.",
    searchPlaceholder: "Firma, Stichwort oder Leistung suchen…",
    regionPlaceholder: "Region",
    searchButton: "Suchen",
    filterTitle: "Filter",
    categoryLabel: "Kategorie",
    minRating: "Mindestbewertung",
    verifiedOnly: "Nur verifizierte Anbieter",
    resetFilters: "Filter zurücksetzen",
    ctaEyebrow: "Schneller geht's so",
    ctaTitle: "Anfrage eingeben und Offerten erhalten",
    ctaText: "Beschreiben Sie Ihr Projekt, passende Auftragnehmer melden sich bei Ihnen.",
    ctaButton: "Jetzt starten",
    registerButton: "Als Auftragnehmer registrieren",
    registerHint: "Eigenes Unternehmen eintragen und von Begner prüfen lassen.",
    pricingLink: "Mitgliedschaften & Preise ansehen",
    found: "Auftragnehmer gefunden",
    sortPlaceholder: "Sortieren",
    sortOptions: [
      { value: "relevance", label: "Relevanz" },
      { value: "rating", label: "Beste Bewertung" },
      { value: "reviews", label: "Meiste Bewertungen" },
      { value: "experience", label: "Erfahrung" },
    ],
    emptyTitle: "Keine Auftragnehmer gefunden.",
    emptyText: "Passen Sie die Filter an oder erweitern Sie die Region.",
    verified: "verifiziert",
    yearsExperience: "Jahre Erfahrung",
    reviews: "Bewertungen",
    requestQuote: "Offerte anfragen",
    viewProfile: "Profil ansehen",
    categories: [
      { id: "bauunternehmen", label: "Bauunternehmen" },
      { id: "reinigung", label: "Reinigung" },
      { id: "raeumung", label: "Räumung & Rückbau" },
    ],
    cantons: [
      { id: "all", label: "Alle Kantone" },
      { id: "ag", label: "Aargau" },
      { id: "ar", label: "Appenzell Ausserrhoden" },
      { id: "ai", label: "Appenzell Innerrhoden" },
      { id: "bl", label: "Basel-Landschaft" },
      { id: "bs", label: "Basel-Stadt" },
      { id: "be", label: "Bern" },
      { id: "fr", label: "Freiburg" },
      { id: "ge", label: "Genf" },
      { id: "gl", label: "Glarus" },
      { id: "gr", label: "Graubünden" },
      { id: "ju", label: "Jura" },
      { id: "lu", label: "Luzern" },
      { id: "ne", label: "Neuenburg" },
      { id: "nw", label: "Nidwalden" },
      { id: "ow", label: "Obwalden" },
      { id: "sh", label: "Schaffhausen" },
      { id: "sz", label: "Schwyz" },
      { id: "so", label: "Solothurn" },
      { id: "sg", label: "St. Gallen" },
      { id: "ti", label: "Tessin" },
      { id: "tg", label: "Thurgau" },
      { id: "ur", label: "Uri" },
      { id: "vd", label: "Waadt" },
      { id: "vs", label: "Wallis" },
      { id: "zg", label: "Zug" },
      { id: "zh", label: "Zürich" },
    ],
    contractors: [
      {
        name: "SupaNex",
        categoryId: "raeumung",
        categoryIds: ["raeumung", "reinigung"],
        category: "Räumung & Reinigung",
        city: "Basel",
        cantonId: "bs",
        cantonIds: ["bs", "bl", "ag", "so"],
        canton: "Basel-Stadt",
        serviceArea: "Basel, Baselland, Aargau & Solothurn",
        rating: 5,
        reviewSource: "Google-Rezensionen",
        reviewsUrl: "https://www.google.com/maps/search/?api=1&query=SupaNex%20Basel",
        verified: false,
        years: 15,
        description:
          "Räumungen, Haus- und Wohnungsräumungen sowie Umzugs- und Baureinigungen mit Abnahmegarantie.",
        tags: ["Räumungen", "Umzugsreinigung", "Baureinigung", "Abnahmegarantie"],
        phone: "+41 61 551 60 70",
        email: "info@supanex.ch",
        address: "Rebgasse 31, 4058 Basel",
        website: "https://supanex.ch/",
      },
      {
        name: "Mara Services GmbH",
        categoryId: "raeumung",
        category: "Räumung & Rückbau",
        city: "Basel",
        cantonId: "bs",
        cantonIds: ["bs", "bl", "ag", "so"],
        canton: "Basel-Stadt",
        serviceArea: "Basel, Baselland, Aargau & Solothurn",
        rating: 5,
        reviewSource: "Google-Rezensionen",
        reviewsUrl:
          "https://www.google.com/maps/search/?api=1&query=Mara%20Services%20GmbH%20Basel&query_place_id=ChIJ5xPANw25kUcRKMm_gmFIKew",
        verified: false,
        years: 5,
        description: "Räumungen, Rückbau und Entkernung in der Nordwestschweiz.",
        tags: ["Räumungen", "Rückbau", "Entkernung"],
        phone: "+41 77 508 08 68",
        email: "info@mara-services.ch",
        address: "Bärenfelserstrasse 40, 4057 Basel",
        website: "https://mara-services.ch/",
      },
      {
        name: "Harta Bau GmbH",
        categoryId: "bauunternehmen",
        category: "General- & Totalunternehmen",
        city: "Basel",
        cantonId: "bs",
        cantonIds: ["bs", "bl"],
        canton: "Basel-Stadt",
        serviceArea: "Region Basel",
        rating: 5,
        reviewSource: "Google-Rezensionen",
        reviewsUrl: "https://www.google.com/maps/search/?api=1&query=Harta%20Bau%20GmbH%20Basel",
        verified: false,
        description:
          "Bauprojekte von der ersten Idee bis zur schlüsselfertigen Übergabe – mit Gesamtbauleitung, Projektentwicklung, Projektsteuerung und Bauherrenvertretung.",
        tags: [
          "Gesamtbauleitung",
          "Generalunternehmen",
          "Totalunternehmen",
          "Projektentwicklung",
          "Bauherrenvertretung",
          "Immobilienentwicklung",
        ],
        phone: "+41 79 838 95 65",
        email: "info@hartabau.ch",
        address: "Klingentalstrasse 47, 4057 Basel",
        website: "https://hartabau.ch/",
      },
    ] as Contractor[],
  },
  fr: {
    eyebrow: "Annuaire",
    title: "Prestataires vérifiés en Suisse",
    introBeforeCount: "Comparez",
    introAfterCount:
      "entreprises du bâtiment selon de vrais avis clients, l'expérience et la région. Demandez gratuitement des offres sans engagement.",
    searchPlaceholder: "Rechercher une entreprise, un mot-clé ou une prestation…",
    regionPlaceholder: "Région",
    searchButton: "Rechercher",
    filterTitle: "Filtres",
    categoryLabel: "Catégorie",
    minRating: "Note minimale",
    verifiedOnly: "Prestataires vérifiés uniquement",
    resetFilters: "Réinitialiser les filtres",
    ctaEyebrow: "Plus rapide ainsi",
    ctaTitle: "Publier un mandat et recevoir des offres",
    ctaText: "Décrivez votre projet, des prestataires adaptés vous contactent.",
    ctaButton: "Commencer",
    registerButton: "S'inscrire comme prestataire",
    registerHint: "Inscrire son entreprise et la faire vérifier par Begner.",
    pricingLink: "Voir les abonnements et les prix",
    found: "prestataires trouvés",
    sortPlaceholder: "Trier",
    sortOptions: [
      { value: "relevance", label: "Pertinence" },
      { value: "rating", label: "Meilleure note" },
      { value: "reviews", label: "Plus d'avis" },
      { value: "experience", label: "Expérience" },
    ],
    emptyTitle: "Aucun prestataire trouvé.",
    emptyText: "Ajustez les filtres ou élargissez la région.",
    verified: "vérifié",
    yearsExperience: "ans d'expérience",
    reviews: "avis",
    requestQuote: "Demander une offre",
    viewProfile: "Voir le profil",
    categories: [
      { id: "bauunternehmen", label: "Entreprise de construction" },
      { id: "reinigung", label: "Nettoyage" },
      { id: "raeumung", label: "Débarras & déconstruction" },
    ],
    cantons: [
      { id: "all", label: "Tous les cantons" },
      { id: "ag", label: "Argovie" },
      { id: "ai", label: "Appenzell Rhodes-Intérieures" },
      { id: "ar", label: "Appenzell Rhodes-Extérieures" },
      { id: "bl", label: "Bâle-Campagne" },
      { id: "bs", label: "Bâle-Ville" },
      { id: "be", label: "Berne" },
      { id: "fr", label: "Fribourg" },
      { id: "ge", label: "Genève" },
      { id: "gl", label: "Glaris" },
      { id: "gr", label: "Grisons" },
      { id: "ju", label: "Jura" },
      { id: "lu", label: "Lucerne" },
      { id: "ne", label: "Neuchâtel" },
      { id: "nw", label: "Nidwald" },
      { id: "ow", label: "Obwald" },
      { id: "sg", label: "Saint-Gall" },
      { id: "sh", label: "Schaffhouse" },
      { id: "sz", label: "Schwytz" },
      { id: "so", label: "Soleure" },
      { id: "tg", label: "Thurgovie" },
      { id: "ti", label: "Tessin" },
      { id: "ur", label: "Uri" },
      { id: "vs", label: "Valais" },
      { id: "vd", label: "Vaud" },
      { id: "zg", label: "Zoug" },
      { id: "zh", label: "Zurich" },
    ],
    contractors: [] as Contractor[],
  },
  it: {
    eyebrow: "Elenco",
    title: "Imprese verificate in Svizzera",
    introBeforeCount: "Confronti",
    introAfterCount:
      "imprese edili in base a recensioni reali, esperienza e regione. Richieda gratuitamente offerte senza impegno.",
    searchPlaceholder: "Cerca impresa, parola chiave o prestazione…",
    regionPlaceholder: "Regione",
    searchButton: "Cerca",
    filterTitle: "Filtri",
    categoryLabel: "Categoria",
    minRating: "Valutazione minima",
    verifiedOnly: "Solo fornitori verificati",
    resetFilters: "Reimposta filtri",
    ctaEyebrow: "Così è più veloce",
    ctaTitle: "Pubblica un incarico e ricevi offerte",
    ctaText: "Descriva il progetto, imprese adatte la contatteranno.",
    ctaButton: "Inizia ora",
    registerButton: "Registrarsi come impresa",
    registerHint: "Inserire la propria azienda e farla verificare da Begner.",
    pricingLink: "Vedi abbonamenti e prezzi",
    found: "imprese trovate",
    sortPlaceholder: "Ordina",
    sortOptions: [
      { value: "relevance", label: "Rilevanza" },
      { value: "rating", label: "Migliore valutazione" },
      { value: "reviews", label: "Più recensioni" },
      { value: "experience", label: "Esperienza" },
    ],
    emptyTitle: "Nessuna impresa trovata.",
    emptyText: "Modifichi i filtri o ampli la regione.",
    verified: "verificato",
    yearsExperience: "anni di esperienza",
    reviews: "recensioni",
    requestQuote: "Richiedi offerta",
    viewProfile: "Vedi profilo",
    categories: [
      { id: "bauunternehmen", label: "Impresa di costruzioni" },
      { id: "reinigung", label: "Pulizia" },
      { id: "raeumung", label: "Sgombero & demolizione" },
    ],
    cantons: [
      { id: "all", label: "Tutti i cantoni" },
      { id: "ag", label: "Argovia" },
      { id: "ar", label: "Appenzello Esterno" },
      { id: "ai", label: "Appenzello Interno" },
      { id: "bl", label: "Basilea Campagna" },
      { id: "bs", label: "Basilea Città" },
      { id: "be", label: "Berna" },
      { id: "fr", label: "Friburgo" },
      { id: "ge", label: "Ginevra" },
      { id: "gl", label: "Glarona" },
      { id: "gr", label: "Grigioni" },
      { id: "ju", label: "Giura" },
      { id: "lu", label: "Lucerna" },
      { id: "ne", label: "Neuchâtel" },
      { id: "nw", label: "Nidvaldo" },
      { id: "ow", label: "Obvaldo" },
      { id: "sg", label: "San Gallo" },
      { id: "sh", label: "Sciaffusa" },
      { id: "so", label: "Soletta" },
      { id: "sz", label: "Svitto" },
      { id: "ti", label: "Ticino" },
      { id: "tg", label: "Turgovia" },
      { id: "ur", label: "Uri" },
      { id: "vs", label: "Vallese" },
      { id: "vd", label: "Vaud" },
      { id: "zg", label: "Zugo" },
      { id: "zh", label: "Zurigo" },
    ],
    contractors: [] as Contractor[],
  },
  en: {
    eyebrow: "Directory",
    title: "Verified contractors in Switzerland",
    introBeforeCount: "Compare",
    introAfterCount:
      "construction companies by genuine customer reviews, experience and region. Request free, no-obligation quotes.",
    searchPlaceholder: "Search company, keyword or service…",
    regionPlaceholder: "Region",
    searchButton: "Search",
    filterTitle: "Filters",
    categoryLabel: "Category",
    minRating: "Minimum rating",
    verifiedOnly: "Verified providers only",
    resetFilters: "Reset filters",
    ctaEyebrow: "An even quicker way",
    ctaTitle: "Post a job and receive quotes",
    ctaText: "Describe your project and suitable contractors will contact you.",
    ctaButton: "Get started",
    registerButton: "Register as a contractor",
    registerHint: "List your company and have it verified by Begner.",
    pricingLink: "View memberships and pricing",
    found: "contractors found",
    sortPlaceholder: "Sort",
    sortOptions: [
      { value: "relevance", label: "Relevance" },
      { value: "rating", label: "Best rating" },
      { value: "reviews", label: "Most reviews" },
      { value: "experience", label: "Experience" },
    ],
    emptyTitle: "No contractors found.",
    emptyText: "Adjust the filters or broaden the region.",
    verified: "verified",
    yearsExperience: "years of experience",
    reviews: "reviews",
    requestQuote: "Request a quote",
    viewProfile: "View profile",
    categories: [
      { id: "bauunternehmen", label: "Construction company" },
      { id: "reinigung", label: "Cleaning" },
      { id: "raeumung", label: "Clearance & demolition" },
    ],
    cantons: [
      { id: "all", label: "All cantons" },
      { id: "ag", label: "Aargau" },
      { id: "ar", label: "Appenzell Ausserrhoden" },
      { id: "ai", label: "Appenzell Innerrhoden" },
      { id: "bl", label: "Basel-Landschaft" },
      { id: "bs", label: "Basel-Stadt" },
      { id: "be", label: "Bern" },
      { id: "fr", label: "Fribourg" },
      { id: "ge", label: "Geneva" },
      { id: "gl", label: "Glarus" },
      { id: "gr", label: "Graubünden" },
      { id: "ju", label: "Jura" },
      { id: "lu", label: "Lucerne" },
      { id: "ne", label: "Neuchâtel" },
      { id: "nw", label: "Nidwalden" },
      { id: "ow", label: "Obwalden" },
      { id: "sg", label: "St. Gallen" },
      { id: "sh", label: "Schaffhausen" },
      { id: "so", label: "Solothurn" },
      { id: "sz", label: "Schwyz" },
      { id: "ti", label: "Ticino" },
      { id: "tg", label: "Thurgau" },
      { id: "ur", label: "Uri" },
      { id: "vs", label: "Valais" },
      { id: "vd", label: "Vaud" },
      { id: "zg", label: "Zug" },
      { id: "zh", label: "Zurich" },
    ],
    contractors: [] as Contractor[],
  },
};

contractorCopy.fr.contractors = [
  {
    ...contractorCopy.de.contractors[0],
    category: "Débarras & nettoyage",
    serviceArea: "Bâle, Bâle-Campagne, Argovie & Soleure",
    reviewSource: "Avis Google",
    description:
      "Débarras de maisons et d'appartements ainsi que nettoyages de déménagement et de chantier avec garantie de remise.",
    tags: ["Débarras", "Nettoyage de déménagement", "Nettoyage de chantier", "Garantie"],
  },
  {
    ...contractorCopy.de.contractors[1],
    category: "Débarras & déconstruction",
    serviceArea: "Bâle, Bâle-Campagne, Argovie & Soleure",
    reviewSource: "Avis Google",
    description: "Débarras, déconstruction et curage dans le nord-ouest de la Suisse.",
    tags: ["Débarras", "Déconstruction", "Curage"],
  },
  {
    ...contractorCopy.de.contractors[2],
    category: "Entreprise générale & totale",
    serviceArea: "Région de Bâle",
    reviewSource: "Avis Google",
    description:
      "Projets de construction de la première idée à la remise clés en main, avec direction générale des travaux, développement et pilotage de projet ainsi que représentation du maître d'ouvrage.",
    tags: [
      "Direction des travaux",
      "Entreprise générale",
      "Entreprise totale",
      "Développement de projets",
      "Représentation du maître d'ouvrage",
      "Développement immobilier",
    ],
  },
];

contractorCopy.it.contractors = [
  {
    ...contractorCopy.de.contractors[0],
    category: "Sgombero & pulizia",
    serviceArea: "Basilea, Basilea Campagna, Argovia & Soletta",
    reviewSource: "Recensioni Google",
    description:
      "Sgomberi di case e appartamenti, pulizie di trasloco e di cantiere con garanzia di consegna.",
    tags: ["Sgomberi", "Pulizia trasloco", "Pulizia cantiere", "Garanzia"],
  },
  {
    ...contractorCopy.de.contractors[1],
    category: "Sgombero & demolizione",
    serviceArea: "Basilea, Basilea Campagna, Argovia & Soletta",
    reviewSource: "Recensioni Google",
    description: "Sgomberi, demolizioni e sventramenti nella Svizzera nord-occidentale.",
    tags: ["Sgomberi", "Demolizione", "Sventramento"],
  },
  {
    ...contractorCopy.de.contractors[2],
    category: "Impresa generale & totale",
    serviceArea: "Regione di Basilea",
    reviewSource: "Recensioni Google",
    description:
      "Progetti edilizi dalla prima idea alla consegna chiavi in mano, con direzione generale dei lavori, sviluppo e gestione del progetto e rappresentanza del committente.",
    tags: [
      "Direzione lavori",
      "Impresa generale",
      "Impresa totale",
      "Sviluppo progetti",
      "Rappresentanza committente",
      "Sviluppo immobiliare",
    ],
  },
];

contractorCopy.en.contractors = [
  {
    ...contractorCopy.de.contractors[0],
    category: "Clearance & cleaning",
    serviceArea: "Basel, Basel-Landschaft, Aargau & Solothurn",
    reviewSource: "Google reviews",
    description:
      "House and apartment clearances plus end-of-tenancy and construction cleaning with handover guarantee.",
    tags: ["Clearance", "End-of-tenancy cleaning", "Construction cleaning", "Guarantee"],
  },
  {
    ...contractorCopy.de.contractors[1],
    category: "Clearance & demolition",
    serviceArea: "Basel, Basel-Landschaft, Aargau & Solothurn",
    reviewSource: "Google reviews",
    description: "Clearance, demolition and strip-out services in north-western Switzerland.",
    tags: ["Clearance", "Demolition", "Strip-out"],
  },
  {
    ...contractorCopy.de.contractors[2],
    category: "General & total contractor",
    serviceArea: "Basel region",
    reviewSource: "Google reviews",
    description:
      "Construction projects from the initial idea to turnkey handover, including overall site management, project development, project control and client representation.",
    tags: [
      "Site management",
      "General contractor",
      "Total contractor",
      "Project development",
      "Client representation",
      "Property development",
    ],
  },
];

const tradeLabels = {
  fr: [
    "Entreprise de construction",
    "Peintres & plâtriers",
    "Sanitaire & chauffage",
    "Électriciens",
    "Sols & parquet",
    "Toiture & ferblanterie",
    "Menuiserie & fenêtres",
    "Paysagistes",
    "Nettoyage",
    "Débarras & déconstruction",
  ],
  it: [
    "Imprese edili",
    "Pittori & gessatori",
    "Sanitari & riscaldamento",
    "Elettricisti",
    "Pavimenti & parquet",
    "Tetti & lattonieri",
    "Falegnami & finestre",
    "Giardinaggio",
    "Pulizia",
    "Sgombero & demolizione",
  ],
  en: [
    "Construction companies",
    "Painters & plasterers",
    "Plumbing & heating",
    "Electricians",
    "Flooring & parquet",
    "Roofing & sheet metal",
    "Joinery & windows",
    "Landscaping",
    "Cleaning",
    "Clearance & demolition",
  ],
};
contractorCopy.de.categories = directoryTrades.map((trade) => ({
  id: trade.slug,
  label: trade.name,
}));
for (const locale of ["fr", "it", "en"] as const) {
  contractorCopy[locale].categories = directoryTrades.map((trade, index) => ({
    id: trade.slug,
    label: tradeLabels[locale][index],
  }));
}

function AuftragnehmerPage() {
  const { language } = useLanguage();
  const search = Route.useSearch();
  const copy = contractorCopy[language];
  const { providers, unavailable } = Route.useLoaderData();
  const contractors = useMemo<Contractor[]>(
    () =>
      providers.map((provider) => {
        const categoryIds = directoryTrades
          .filter((trade) => providerMatchesSpecialty(provider, trade))
          .map((trade) => trade.slug);
        const cantonIds = providerCantonIds(provider);
        return {
          id: provider.id,
          name: provider.name,
          categoryId: categoryIds[0] ?? "other",
          categoryIds,
          category: provider.category || "Bau-Auftragnehmer",
          city: provider.location,
          cantonId: cantonIds[0] ?? "all",
          cantonIds,
          canton: provider.canton ?? "",
          rating: provider.rating ?? 0,
          reviews: provider.review_count,
          verified: provider.verified,
          description: provider.description,
          tags: provider.services ?? [],
          phone: provider.phone ?? undefined,
          email: provider.email ?? undefined,
          website: providerWebsite(provider.website),
        };
      }),
    [providers],
  );
  const initialCategory = copy.categories.some((category) => category.id === search.category)
    ? search.category
    : undefined;
  const initialCanton = copy.cantons.some((c) => c.id === search.canton) ? search.canton : "all";
  const [query, setQuery] = useState(search.q ?? "");
  const [canton, setCanton] = useState(initialCanton ?? "all");
  const [activeCategories, setActiveCategories] = useState<string[]>(
    initialCategory ? [initialCategory] : [],
  );
  const [minRating, setMinRating] = useState<number[]>([0]);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [sort, setSort] = useState("relevance");

  const filtered = useMemo(() => {
    let list = contractors.filter((c) => {
      const cantonIds = c.cantonIds ?? [c.cantonId];
      const categoryIds = c.categoryIds ?? [c.categoryId];
      if (canton !== "all" && !cantonIds.includes(canton)) return false;
      if (activeCategories.length && !activeCategories.some((id) => categoryIds.includes(id)))
        return false;
      if (c.rating < minRating[0]) return false;
      if (onlyVerified && !c.verified) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        const hit =
          c.name.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          c.canton.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          c.serviceArea?.toLowerCase().includes(q) ||
          c.address?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q));
        if (!hit) return false;
      }
      return true;
    });
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    if (sort === "reviews") list = [...list].sort((a, b) => (b.reviews ?? 0) - (a.reviews ?? 0));
    if (sort === "experience") list = [...list].sort((a, b) => (b.years ?? 0) - (a.years ?? 0));
    return list;
  }, [contractors, query, canton, activeCategories, minRating, onlyVerified, sort]);

  const toggleCategory = (c: string) =>
    setActiveCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  return (
    <>
      {/* Page header */}
      <section className="border-b border-border bg-surface">
        <div className="container-page py-9 md:py-16">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
            <SectionHeading
              eyebrow={copy.eyebrow}
              title={copy.title}
              description={
                <>
                  {copy.introBeforeCount} {contractors.length} {copy.introAfterCount}
                </>
              }
              level="page"
              className="max-w-2xl"
            />

            <aside className="border-l-2 border-brand py-1 pl-4 sm:pl-6 lg:mb-1">
              <p className="text-base leading-7 text-muted-foreground">{copy.registerHint}</p>
              <Button
                asChild
                size="lg"
                className="mt-4 h-12 w-full rounded-lg px-6 text-sm font-semibold shadow-none sm:w-auto"
              >
                <Link to="/registrieren" search={{ typ: "auftragnehmer" }}>
                  {copy.registerButton}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Link
                to="/anbieter-preise"
                search={{ kategorie: "auftragnehmer" }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-deep underline-offset-4 hover:underline"
              >
                {copy.pricingLink}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </aside>
          </div>

          <div className="mt-10 grid gap-3 rounded-2xl border border-border bg-card p-3 shadow-card md:grid-cols-[1.4fr_1fr_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={copy.searchPlaceholder}
                className="h-12 rounded-xl border-border pl-12"
              />
            </div>
            <Select value={canton} onValueChange={setCanton}>
              <SelectTrigger className="h-12 rounded-xl">
                <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder={copy.regionPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {copy.cantons.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="lg" className="h-12 w-full rounded-xl px-6 font-semibold md:w-auto">
              {copy.searchButton}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Listing */}
      <section className="bg-background py-8 md:py-12">
        <div className="container-page grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* Sidebar filters */}
          <aside className="space-y-6">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-6">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-brand-deep" />
                <h2 className="font-display text-lg font-bold">{copy.filterTitle}</h2>
              </div>

              <div className="mt-6">
                <p className="text-sm font-semibold text-foreground">{copy.categoryLabel}</p>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 lg:block lg:space-y-2">
                  {copy.categories.map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-3 text-sm text-foreground"
                    >
                      <Checkbox
                        checked={activeCategories.includes(c.id)}
                        onCheckedChange={() => toggleCategory(c.id)}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-6 border-t border-border pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{copy.minRating}</p>
                  <span className="text-sm font-bold text-brand-deep">
                    {minRating[0].toFixed(1)}
                  </span>
                </div>
                <Slider
                  value={minRating}
                  onValueChange={setMinRating}
                  min={0}
                  max={5}
                  step={0.5}
                  className="mt-4"
                />
              </div>

              <div className="mt-6 border-t border-border pt-6">
                <Label
                  htmlFor="verified"
                  className="flex cursor-pointer items-center gap-3 text-sm text-foreground"
                >
                  <Checkbox
                    id="verified"
                    checked={onlyVerified}
                    onCheckedChange={(v) => setOnlyVerified(Boolean(v))}
                  />
                  <span>{copy.verifiedOnly}</span>
                </Label>
              </div>

              <Button
                variant="outline"
                className="mt-6 w-full"
                onClick={() => {
                  setActiveCategories([]);
                  setMinRating([0]);
                  setOnlyVerified(false);
                  setCanton("all");
                  setQuery("");
                }}
              >
                {copy.resetFilters}
              </Button>
            </div>

            <div className="rounded-2xl bg-gradient-brand p-6 text-primary-foreground shadow-brand">
              <p className="text-sm font-semibold uppercase tracking-wider opacity-80">
                {copy.ctaEyebrow}
              </p>
              <h3 className="mt-2 font-display text-xl font-bold">{copy.ctaTitle}</h3>
              <p className="mt-2 text-sm opacity-90">{copy.ctaText}</p>
              <Button asChild variant="secondary" className="mt-4 w-full font-semibold">
                <Link to="/auftrag-einstellen">{copy.ctaButton}</Link>
              </Button>
            </div>
          </aside>

          {/* Results */}
          <div>
            <div className="flex flex-col items-stretch justify-between gap-3 min-[420px]:flex-row min-[420px]:items-center">
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">{filtered.length}</span> {copy.found}
              </p>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-full min-[420px]:w-[200px]">
                  <SelectValue placeholder={copy.sortPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {copy.sortOptions.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-6 space-y-4">
              {filtered.map((c) => (
                <ContractorCard key={c.id ?? c.name} c={c} copy={copy} />
              ))}
              {filtered.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
                  <p className="font-semibold text-foreground">
                    {unavailable && language === "de"
                      ? "Die Anbieterprofile sind momentan nicht abrufbar."
                      : copy.emptyTitle}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">{copy.emptyText}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <DirectoryNavigation kind="contractor" />
    </>
  );
}

type ContractorCopy = (typeof contractorCopy)["de"];

function ContractorCard({ c, copy }: { c: Contractor; copy: ContractorCopy }) {
  const initials = c.name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  const ratingContent = (
    <>
      <div className="flex items-center gap-1 text-[#fbbc04]">
        <Star className="h-5 w-5 fill-current" />
        <span className="font-display text-2xl font-bold text-foreground">
          {c.rating.toFixed(1)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {c.reviews !== undefined && `${c.reviews} ${copy.reviews}`}
        {c.reviews !== undefined && c.reviewSource && " · "}
        {c.reviewSource}
      </p>
    </>
  );
  return (
    <article
      id={c.id ? `anbieter-${c.id}` : undefined}
      className="group grid gap-6 rounded-2xl border border-border bg-card p-6 shadow-card transition hover:border-brand md:grid-cols-[auto_1fr_auto]"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-brand font-display text-2xl font-bold text-primary-foreground shadow-brand">
        {initials}
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display text-xl font-bold text-foreground">{c.name}</h3>
          {c.verified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-0.5 text-xs font-semibold text-brand-deep">
              <BadgeCheck className="h-3.5 w-3.5" /> {copy.verified}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {c.serviceArea ?? `${c.city}, ${c.canton}`}
          </span>
          <span>·</span>
          <span>{c.category}</span>
          {c.years !== undefined && (
            <>
              <span>·</span>
              <span>
                {c.years} {copy.yearsExperience}
              </span>
            </>
          )}
        </div>
        <p className="mt-3 text-foreground">{c.description}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {c.tags.map((t) => (
            <Badge key={t} variant="secondary" className="font-medium">
              {t}
            </Badge>
          ))}
        </div>
        {(c.phone || c.email || c.address) && (
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
            {c.phone && (
              <a
                className="inline-flex items-center gap-1 hover:text-brand-deep"
                href={`tel:${c.phone}`}
              >
                <Phone className="h-3.5 w-3.5" /> {c.phone}
              </a>
            )}
            {c.email && (
              <a
                className="inline-flex items-center gap-1 hover:text-brand-deep"
                href={`mailto:${c.email}`}
              >
                <Mail className="h-3.5 w-3.5" /> {c.email}
              </a>
            )}
            {c.address && <span>{c.address}</span>}
          </div>
        )}
      </div>
      <div className="flex flex-col items-start justify-between gap-4 md:items-end">
        {c.reviews && c.reviews > 0 ? (
          c.reviewsUrl ? (
            <a
              href={c.reviewsUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`${c.name}: ${c.rating.toFixed(1)} ${c.reviewSource}`}
              className="rounded-lg p-1 text-right transition hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {ratingContent}
            </a>
          ) : (
            <div className="text-right">{ratingContent}</div>
          )
        ) : null}
        <div className="flex w-full flex-col gap-2 md:w-auto">
          <Button asChild size="sm" className="font-semibold">
            <Link to="/auftrag-einstellen">
              {copy.requestQuote}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          {c.website ? (
            <Button asChild size="sm" variant="outline">
              <a href={c.website} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" /> {copy.viewProfile}
              </a>
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

// keep lucide imports referenced for tree-shaking-friendly bundling
void CheckCircle2;
